/**
 * @module Components.Subscription
 *
 */
import React, { useState, useEffect, useMemo } from "react";
import { Redirect } from "react-router-dom";
import { cls } from "app/utils";
import { UserState } from "app/reducers/user";
import { UserModel } from "app/models";
import { Spinner } from "app/components/Loading";
import { Loc } from "app/components/helpers";
import { useLoc, useActions } from "app/utils/hooks";
import { updateSubscription } from "app/actions/stripe";
import { fetchSelf } from "app/actions/user";
import UpgradeErrorModal from "app/components/modals/UpgradeErrorModal";
import PlanTypes, { PLAN_TYPES } from "./../Upgrade/PlanTypes";
import SuccessPage from "./../Upgrade/SuccessPage";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl";
import { openIntercom } from "app/utils/intercom";
import styles from "./styles.module.scss";
import LOCALES from "./locale.js";
import { handleUserUTMParams } from "app/utils/handleUserUTMParams";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/reducers";
import { usePromoOffer } from "app/hooks/usePromoOffer";
import { getDiscountedAmount } from "app/utils/planPricing";
import { parseCurrency } from "app/utils/currency";
import { repriceTargets } from "app/utils/subscription";
import { fetchPlans } from "app/actions/subscription-plan";
import PromoResolveError from "app/components/PromoResolveError";

type PropsDef = {
  self: UserState;
};

// The plans this page sells, in the order it lists them: the top plan on the
// yearly term comes first and is the one selected unless the link asks for
// another. An academy is not among them: it stays with support.
const PRO_PLANS = ["coachpro+yearly", "coachpro+monthly"];
const PLUS_AND_PRO_PLANS = [...PRO_PLANS, "coach+yearly", "coach+monthly"];
const SOLD_PLANS = [...PLUS_AND_PRO_PLANS, "analyze+yearly", "analyze+monthly"];

// The plans the current one may move to. A Stripe plan takes the API's list
// (User::Plans::Base#upgrade_targets): the other term, or any plan above it.
// A Recurly Plus or Analyze plan still upgrades to PRO through Recurly — an
// Analyze plan to Plus as well — as before. Anything else is support's.
const upgradeTargets = (user: UserModel): string[] => {
  if (user.plan.source === "Stripe") {
    return repriceTargets(user, SOLD_PLANS);
  }
  if (user.plan.canUpgrade) {
    return user.plan.planCode.startsWith("analyze") ? PLUS_AND_PRO_PLANS : PRO_PLANS;
  }
  return [];
};

// "coachpro" of "coachpro+yearly": the plan regardless of its term
const planFamily = (planCode: string) => planCode?.split("+")[0];

export default function Status(props: PropsDef) {
  const actions = useActions({ updateSubscription, fetchSelf, fetchPlans });
  const dispatch = useDispatch();
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string>(null);
  const [back, setBack] = useState<boolean>(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const { formatNumber } = useLoc();

  const user = props.self.data;
  const plan = user.plan;
  const targets = useMemo(() => upgradeTargets(user), [user]);
  // The subscription is billed in one currency: the prices, the code and the
  // update all use it.
  const currency = parseCurrency(plan.currency);
  // The plan list is shared store state: what it holds is usable only once it
  // is priced in this currency (PlanTypes fetches it)
  const plans = useSelector((state: RootState) => state.subscriptionPlan);
  const plansReady = !plans.pending && plans.currency === currency && plans.data != null;
  // Without prices there is nothing to pick from: say so and offer a retry
  // rather than leave an empty list and a dead button.
  const plansError = Boolean(plans.error) && !plans.pending && !plansReady;

  // The coupon and the plan the link asks for — read as text: a code such as
  // "0123" is a code, not a number. A code is a Stripe discount, so it rides
  // only onto a Stripe plan: a Recurly plan upgrades through Recurly, which
  // knows nothing of it.
  const search = new URLSearchParams(window.location.search);
  const linkedCoupon =
    plan.source === "Stripe" ? search.get("coupon") || undefined : undefined;
  // The code the page resolves: the link's to begin with, or whatever is typed
  // into the field below (and nothing, once a code locked elsewhere is dropped).
  const [coupon, setCoupon] = useState<string>(linkedCoupon);
  const [couponDraft, setCouponDraft] = useState<string>(linkedCoupon || "");
  // Applying is a fresh judgement: the latch below opens so the code's own
  // plan is followed again, even when the same code was just cleared.
  const applyCoupon = () => {
    setFollowedLockOf(undefined);
    setCoupon(couponDraft.trim() || undefined);
  };
  const [upgradePlan, setUpgradePlan] = useState<string>(() => {
    const linkedPlan = search.get("plan");
    return targets.includes(linkedPlan) ? linkedPlan : targets[0];
  });
  const selectedPlanType = PLAN_TYPES.find(
    (planType) => planType.id === upgradePlan,
  );

  // What the code grants on the selected plan, resolved by the API the way the
  // checkout pages resolve it: a campaign code or a bare Stripe coupon alike.
  const {
    offer,
    baseAmount,
    loading: offerLoading,
    error: offerError,
    lockedPlan,
    resolveOffer,
  } = usePromoOffer({
    code: coupon,
    planCode: upgradePlan || "",
    currency,
    ready: Boolean(coupon && upgradePlan),
  });

  // A term-locked code resolves to its own plan: follow it once, so the
  // discount shows on the plan it belongs to and a plan picked afterwards
  // still holds. A lock onto a plan not on offer here is dropped with its
  // code, as the API drops it: the preview answers for the locked plan, never
  // for the one asked, and the button would wait forever.
  const [followedLockOf, setFollowedLockOf] = useState<string>();
  useEffect(() => {
    if (!lockedPlan || followedLockOf === coupon) {
      return;
    }
    setFollowedLockOf(coupon);
    if (!targets.includes(lockedPlan)) {
      setCoupon(undefined);
    } else if (lockedPlan !== upgradePlan) {
      setUpgradePlan(lockedPlan);
    }
  }, [lockedPlan, coupon, followedLockOf, upgradePlan, targets]);

  // The code belongs to another plan than the one now selected: the preview
  // answers for its own plan and never for this one, so the page says the
  // discount does not apply instead of waiting for an answer that never comes.
  const lockedElsewhere = Boolean(lockedPlan && lockedPlan !== upgradePlan);

  if (back) {
    return <Redirect to="/" />;
  }

  // Ahead of the loading gate: the upgrade refreshes the account, and the
  // success screen must survive that refresh.
  if (upgradeSuccess) {
    return (
      <SuccessPage
        plan={upgradePlan}
        user={user}
        reprice={user.plan.source === "Stripe"}
        onDone={() => setBack(true)}
      />
    );
  }

  if (props.self.pending) {
    return <Spinner />;
  }

  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  const handleBackClick = () => {
    setBack(true);
  };

  const handleUpgradeClick = () => {
    setPending(true);
    setError(null);

    // The API resolves the code itself and drops one that does not apply.
    actions
      .updateSubscription(upgradePlan, coupon || null)
      .then(async (result: any) => {
        if (result.type === "@stripe.post.success") {
          // The plan just changed under the loaded account: refresh it (forced,
          // fetchSelf is a no-op once loaded) so the app's plan gates see the
          // new plan on the way back. A failed refresh is no failed upgrade.
          try {
            await actions.fetchSelf(true);
          } catch (error) {
            console.error("Error refreshing account after the upgrade:", error);
          }
          setPending(false);
          setUpgradeSuccess(true);
        } else if (result.type === "@stripe.post.error") {
          setPending(false);
          setError(result.payload.defaultMessage);
          setShowErrorModal(true);
        }
      });

    handleUserUTMParams(dispatch);
  };

  const changePlanType = (data: any) => {
    if (data !== null) {
      setUpgradePlan(data.id);
    }
  };

  const showDiscount = () => {
    if (!coupon) {
      return null;
    }

    if (lockedElsewhere) {
      return (
        <div className={cls("mt-1")}>
          <div className={styles.totalPrice}>
            <Loc value={LOCALES.coupon_not_allowed} />
          </div>
        </div>
      );
    }

    // The preview did not answer: say so and offer a retry rather than let
    // the upgrade go ahead with a code nobody has seen resolved.
    if (offerError) {
      return <PromoResolveError onRetry={resolveOffer} />;
    }

    if (offerLoading || baseAmount == null) {
      return null;
    }

    // The code is unknown, or grants nothing on the selected plan.
    if (!offer?.discount) {
      return (
        <div className={cls("mt-1")}>
          <div className={styles.totalPrice}>
            <Loc value={LOCALES.coupon_not_allowed} />
          </div>
        </div>
      );
    }

    const total = getDiscountedAmount(baseAmount, offer.discount);
    const planPeriod = upgradePlan.endsWith("yearly") ? "year" : "month";
    const money = { style: "currency", currency: currency.toUpperCase() };

    const subtotal = formatNumber(baseAmount / 100, money);
    const discount = "-" + formatNumber((baseAmount - total) / 100, money);
    const discounted = formatNumber(total / 100, money);
    // A one-off coupon prices the first payment only, a repeating one the
    // first months; after that the plan's own price returns.
    const { duration, durationInMonths } = offer.discount;
    const isOnce = duration === "once";
    const isRepeating = duration === "repeating" && !!durationInMonths;
    const totalDue = isOnce ? discounted : `${discounted} / ${planPeriod}`;
    const priceLabel = isOnce
      ? LOCALES.your_first_payment
      : isRepeating
      ? { ...LOCALES.your_price_for_months, values: { months: durationInMonths } }
      : LOCALES.your_price;

    return (
      <div className={cls("mt-1")}>
        <div className={styles.totalPrice}>
          <h5>
            <span>
              <Loc value={LOCALES.normal_price} />
            </span>
            <span>{subtotal}</span>
          </h5>
          <h5>
            <span>
              <Loc value={LOCALES.your_discount} />
            </span>
            <span>
              <span className={styles.red}>({discount})</span>
            </span>
          </h5>
          <h6>
            <Loc value={priceLabel} />
          </h6>
          <h1>{totalDue}</h1>
          {(isOnce || isRepeating) && (
            <p>
              <Loc
                value={{
                  ...LOCALES.then_normal_price,
                  values: { price: subtotal, period: planPeriod },
                }}
              />
            </p>
          )}
        </div>
      </div>
    );
  };

  // A step up names the plan it unlocks; the other term of the same plan is
  // a switch, with nothing new to unlock.
  const getUpgradeLocale = () => {
    if (planFamily(upgradePlan) === planFamily(plan.planCode)) {
      return {
        title: {
          ...LOCALES.switch_plan_title,
          values: { planName: selectedPlanType.title },
        },
      };
    }
    if (planFamily(upgradePlan) === "coachpro") {
      return {
        title: LOCALES.upgrade_to_pro_title,
        subtitle: LOCALES.upgrade_to_pro_subtitle,
      };
    }
    return {
      title: LOCALES.upgrade_to_plus_title,
      subtitle: LOCALES.upgrade_to_plus_subtitle,
    };
  };

  const showUpgradeOptions = () => {
    const upgradeLocale = getUpgradeLocale();

    return (
      <div>
        <UpgradeErrorModal
          show={showErrorModal}
          error={error}
          onClose={closeErrorModal}
        />

        <div className={cls("mt-1")}>
          <h3 className={styles.title}>
            <Loc value={upgradeLocale.title} />
          </h3>
          {upgradeLocale.subtitle && (
            <p className={styles.subtitle}>
              <FormattedHTMLMessage {...upgradeLocale.subtitle} />
            </p>
          )}

          {plansError && <PromoResolveError onRetry={() => actions.fetchPlans(currency)} />}

          <PlanTypes
            selectPlan={upgradePlan}
            onChange={changePlanType}
            onlyPlans={targets}
            currency={currency}
          />
          {/* A code is a Stripe discount: a Recurly plan upgrades through
              Recurly, which would not honour one, so it is not offered a field. */}
          {plan.source === "Stripe" && (
            <div className={styles.coupon}>
              <input
                type="text"
                className={cls("form-control", styles.coupon_input)}
                value={couponDraft}
                aria-label={LOCALES.promo_code_label.defaultMessage}
                placeholder={LOCALES.promo_code_label.defaultMessage}
                onChange={(event) => setCouponDraft(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && applyCoupon()}
              />
              <button
                type="button"
                className={cls("btn", "btn-outline-secondary")}
                disabled={pending || couponDraft.trim() === (coupon || "")}
                onClick={applyCoupon}
              >
                <Loc value={LOCALES.promo_code_apply} />
              </button>
            </div>
          )}

          {showDiscount()}
        </div>

        <button
          disabled={pending}
          className={cls("btn", "btn-outline-secondary", "pull-left", "mt-1")}
          onClick={handleBackClick}
        >
          <Loc value={LOCALES.back_button} />
        </button>

        <button
          disabled={pending || !plansReady || Boolean(coupon && !lockedElsewhere && (offerLoading || offerError))}
          className={cls("btn", "btn-primary", "pull-right", "mt-1")}
          onClick={handleUpgradeClick}
        >
          <FormattedMessage
            {...LOCALES.upgrade_button}
            values={{ planName: selectedPlanType.title }}
          />
        </button>
      </div>
    );
  };

  const showContactSupport = () => {
    return (
      <div>
        <div className={cls("mt-1", "mb-1")}>
          <FormattedHTMLMessage {...LOCALES.cant_upgrade} />
        </div>

        <button
          className={cls("btn", "btn-secondary", "pull-left", "mt-1")}
          onClick={handleBackClick}
        >
          <Loc value={LOCALES.back_button} />
        </button>

        <button
          className={cls("btn", "btn-primary", "pull-right", "mt-1")}
          onClick={openIntercom}
        >
          <Loc value={LOCALES.contact_support} />
        </button>
      </div>
    );
  };

  return (
    <div className={styles.status}>
      <div className={styles.statusBox}>
        <div className={styles.title}>
          <FormattedMessage
            {...LOCALES.status_active_message}
            values={{ planName: plan.name }}
          />
        </div>

        <div className={styles.info}>
          <div className={styles.rowItem}>
            <span>
              <Loc value={LOCALES.status_plan} />:
            </span>
            <span>{plan.name}</span>
          </div>
          <div className={styles.rowItem}>
            <span>
              <Loc value={LOCALES.status} />:
            </span>
            <span
              className={`${
                user.subscription.status === "active"
                  ? styles.green
                  : styles.red
              }`}
            >
              {user.subscription.status}
            </span>
          </div>
        </div>

        {targets.length > 0 ? showUpgradeOptions() : showContactSupport()}

        <div className={styles.clearFloat}></div>
      </div>
    </div>
  );
}
